import { GameObjects, Scene } from 'phaser';
import type { RoadPoint } from '../domain/roadProjection';

type VehicleRigOptions = {
  texture: string;
  frame: number;
  accent: number;
};

export class RaceVehicleRig {
  readonly container: GameObjects.Container;
  private readonly body: GameObjects.Image;
  private readonly shadow: GameObjects.Ellipse;
  private readonly turbo: GameObjects.Ellipse;
  private readonly wheels: GameObjects.Container[];
  private wheelRotation = 0;

  constructor(scene: Scene, options: VehicleRigOptions) {
    this.container = scene.add.container(0, 0);
    this.shadow = scene.add.ellipse(0, 91, 250, 54, 0x02070c, 0.46);
    this.turbo = scene.add.ellipse(0, 82, 50, 76, options.accent, 0).setBlendMode('ADD');
    this.body = scene.add.image(0, 0, options.texture, options.frame).setDisplaySize(300, 300);
    this.wheels = [-103, 103].map((x) => {
      const wheel = scene.add.container(x, 60);
      const tire = scene.add.ellipse(0, 0, 33, 48, 0x090d12, 0.98).setStrokeStyle(3, 0x283844);
      const hub = scene.add.circle(0, 0, 11, options.accent, 0.92).setStrokeStyle(3, 0xe8fbff, 0.82);
      const spokes = scene.add.graphics();
      spokes.lineStyle(3, 0xd9f8ff, 0.9);
      for (let index = 0; index < 4; index += 1) {
        const angle = index * Math.PI / 2;
        spokes.beginPath().moveTo(Math.cos(angle) * 4, Math.sin(angle) * 4)
          .lineTo(Math.cos(angle) * 13, Math.sin(angle) * 18).strokePath();
      }
      wheel.add([tire, hub, spokes]);
      return wheel;
    });
    this.container.add([this.shadow, this.turbo, this.body, ...this.wheels]);
  }

  setRoadTransform(point: RoadPoint, speed: number, deltaMs: number, turboStrength: number) {
    const interpolation = Math.min(1, deltaMs * 0.006);
    this.container.x += (point.x - this.container.x) * interpolation;
    this.container.y += (point.y - this.container.y) * interpolation;
    this.container.scaleX += (point.scale - this.container.scaleX) * interpolation;
    this.container.scaleY += (point.scale - this.container.scaleY) * interpolation;
    this.container.rotation += (point.rotation - this.container.rotation) * interpolation;
    this.container.setDepth(Math.round(point.y));

    this.wheelRotation += speed * deltaMs * 0.00022;
    this.wheels.forEach((wheel, index) => {
      wheel.rotation = this.wheelRotation * (index === 0 ? 1 : -1);
      wheel.y = 60 + Math.sin(this.wheelRotation * 1.7 + index) * 1.5;
    });
    this.body.y = Math.sin(this.wheelRotation * 0.52) * 2.2 - turboStrength * 3;
    this.body.rotation = Math.sin(this.wheelRotation * 0.19) * 0.006;
    this.shadow.scaleX = 1 + turboStrength * 0.12;
    this.shadow.alpha = 0.46 - turboStrength * 0.1;
    this.turbo.alpha = turboStrength * (0.55 + Math.sin(this.wheelRotation * 3) * 0.18);
    this.turbo.scaleY = 0.8 + turboStrength * 1.2;
  }

  setVisible(visible: boolean) {
    this.container.setVisible(visible);
  }

  destroy() {
    this.container.destroy(true);
  }
}
